import { useEffect, useRef, useState } from "react";
// eslint-disable-next-line import/no-named-as-default
import Map, { MapEvent, MapLayerMouseEvent, MapRef, Marker, ViewState } from "react-map-gl";
import { LocationOnRounded as LocationOn } from "@mui/icons-material";
import { LngLatLike } from "mapbox-gl";
import { Box, styled, SxProps, Theme, useMediaQuery } from "@mui/material";
import { loadMapboxgl, MAPBOX_STYLE, MAPBOX_TOKEN } from "../../config/mapbox";

type MapDialogProps = {
  lat?: any;
  lon?: any;
  location?: any;
  handleChoose?(data: LngLatLike): void;
  sx?: SxProps<Theme>;
  canvasBorderRadius?: string;
  height?: string;
};
const MapDialog = ({ lat, lon, location, handleChoose, sx, canvasBorderRadius, height = "600px" }: MapDialogProps) => {
  const mapRef = useRef<MapRef>();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [locationState, setLocation] = useState<any>(location || {});

  useEffect(() => {
    if (typeof location === "string") {
      setLocation(JSON.parse(location));
    } else {
      setLocation(location);
    }
    console.log("location changed", { location });
  }, [location, lat, lon]);

  // Update map view when location changes. Debounced so that typing
  // lat/lon values doesn't fly the map mid-keystroke.
  useEffect(() => {
    if (!locationState?.lat || !locationState?.lon || !mapRef.current) return;
    const timer = setTimeout(() => {
      mapRef.current?.flyTo({
        center: [Number(locationState.lon), Number(locationState.lat)],
        zoom: 14,
        essential: true,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [locationState]);

  useEffect(() => {
    mapRef.current?.resize();
  });

  const initialViewState: Partial<ViewState> = {
    longitude: locationState?.lon || 81,
    latitude: locationState?.lat || 7,
    zoom: 6,
    bearing: 0,
    pitch: 0,
  };

  const handleMapClick = (e: MapLayerMouseEvent) => {
    console.log(e.lngLat);
    const feature = e.target.queryRenderedFeatures(e.point, { layers: ["countries-layer"] });
    let country = "";
    let name = "";
    if (feature && feature[0] && feature[0].properties) {
      country = feature[0].properties.country;
      name = feature[0].properties.name;
    }
    if (handleChoose) {
      handleChoose({
        lon: e.lngLat.lng,
        lat: e.lngLat.lat,
        country: country,
        name: name,
      } as any);
    }
    setLocation({
      lon: e.lngLat.lng,
      lat: e.lngLat.lat,
    });
  };

  const handleOnLoad = (e: MapEvent) => {
    if (locationState?.lat && locationState?.lon) {
      mapRef.current?.flyTo({
        center: [Number(locationState.lon), Number(locationState.lat)],
        zoom: 14
      });
    }

    e.target.addSource("countries-source", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              name: "South East",
              country: "Sri Lanka",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [81, 5.5],
                  [82, 5.5],
                  [82, 7.8],
                  [81, 7.8],
                  [81, 5.5],
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "South West",
              country: "Sri Lanka",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [79, 5.5],
                  [81, 5.5],
                  [81, 7.8],
                  [79, 7.8],
                  [79, 5.5],
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "North East",
              country: "Sri Lanka",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [80.5, 10],
                  [82, 10],
                  [82, 7.8],
                  [80.5, 7.8],
                  [80.5, 10],
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "North West",
              country: "Sri Lanka",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [79, 10],
                  [79, 7.8],
                  [80.5, 7.8],
                  [80.5, 10],
                  [79, 10],
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "Sumatra",
              country: "Indonesia",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [95.0, 0.0], // Southwest corner
                  [100.0, 0.0], // Southeast corner
                  [100.0, 5.0], // Northeast corner
                  [95.0, 5.0], // Northwest corner
                  [95.0, 0.0], // Close the square
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "Java",
              country: "Indonesia",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [105.0, -8.0], // Southwest corner
                  [110.0, -8.0], // Southeast corner
                  [110.0, -3.0], // Northeast corner
                  [105.0, -3.0], // Northwest corner
                  [105.0, -8.0], // Close the square
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "Borneo",
              country: "Indonesia",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [110.0, -3.0], // Southwest corner
                  [115.0, -3.0], // Southeast corner
                  [115.0, 2.0], // Northeast corner
                  [110.0, 2.0], // Northwest corner
                  [110.0, -3.0], // Close the square
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "Sulawesi",
              country: "Indonesia",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [120.0, -3.0], // Southwest corner
                  [125.0, -3.0], // Southeast corner
                  [125.0, 2.0], // Northeast corner
                  [120.0, 2.0], // Northwest corner
                  [120.0, -3.0], // Close the square
                ],
              ],
            },
          },
          {
            type: "Feature",
            properties: {
              name: "Papua",
              country: "Indonesia",
            },
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [135.0, -6.0], // Southwest corner
                  [140.0, -6.0], // Southeast corner
                  [140.0, -1.0], // Northeast corner
                  [135.0, -1.0], // Northwest corner
                  [135.0, -6.0], // Close the square
                ],
              ],
            },
          },
        ],
      },
    });

    e.target.addLayer({
      layout: {},
      id: "countries-layer",
      type: "fill",
      source: "countries-source",
      paint: {
        "fill-color": "#0080ff",
        "fill-opacity": 0,
      },
    });
  };

  return (
    <MapWrapper
      sx={sx}
      style={{
        ["--canvas-border-radius" as any]: canvasBorderRadius,
        ["--canvas-height" as any]: height,
      }}
    >
      <Map
        ref={mapRef as any}
        style={{
          backgroundColor: "transparent",
          overflow: "hidden",
          width: "100%",
          height,
          position: "relative",
        }}
        id="map-box-container"
        interactiveLayerIds={["map-data-fill", "countries"]}
        interactive={true}
        initialViewState={initialViewState}
        // mapStyle={MAP_STYLE as MapboxStyle}
        // mapStyle="mapbox://styles/mapbox/dark-v9"
        // mapStyle="mapbox://styles/mapbox/satellite-v9"
        mapStyle={MAPBOX_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapLib={loadMapboxgl()}
        onLoad={handleOnLoad}
        cooperativeGestures={isMobile}
        onClick={(e) => {
          if (handleChoose) {
            handleMapClick(e);
          }
        }}
      >
        <Marker
          style={{
            left: 0,
            opacity: 1,
            position: "absolute",
            top: 0,
            transition: "opacity .2s",
            willChange: "transform",
          }}
          latitude={initialViewState.latitude as number}
          longitude={initialViewState.longitude as number}
        >
          {/*<LocationOn fontSize="large" sx={{ color: "#ffb60d" }} />*/}
          <LocationOn fontSize="large" sx={{ color: "success.main" }} />
        </Marker>
      </Map>
    </MapWrapper>
  );
};

const MapWrapper = styled(Box)`
  width: 100%;
  #map-box-container canvas {
    width: 100% !important;
    height: var(--canvas-height, 600px) !important;
    border-radius: var(--canvas-border-radius, 20px 0px 0px 20px);
  }
`;

export default MapDialog;
